/**
 * "ATD — all residents" (2026-10-13). Every CURRENT resident in the caller's
 * scope, whether or not they have a Finance account or a UMED case, so ATD can
 * be marked for anyone (Dominique, Finance, and Josie, UMED: "not every
 * resident reflects in Finance for us to be able to mark ATD ... Every active
 * resident should reflect there"; Isaiah: "need all residents available in
 * ATD").
 *
 * Built on the board kit (components/board; docs/design/boards.md), like the
 * Finance and Scheduling boards: the tabs are the server's ATD buckets with
 * their counts, a demonstrative legend explains each chip with the same
 * component the rows draw, the table scrolls with synced bars top and bottom,
 * and a row opens DOCKED beside the table -- never unfolding in it -- holding
 * the shared AtdPanel, so Mark ATD / Mark dispensed / Change / Resend are the
 * one existing command whichever workspace opens it (Finance's view, UMED's
 * "Mark ATD for any resident").
 *
 * Who is listed, the counts, the order and the page are the server's
 * (GET /atd/residents; services/residents/atdResidents.js). No date of birth:
 * the Finance board does not show one, and this view follows its PHI line.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BoardTabs, BoardTable, BoardLegend, ColumnKey, Pager, StateMark, NotRecorded, SourceTag,
  useBoardUrlState, boardDate, panelId, tabId,
} from '../board';
import type { BoardColumn, BoardTabDef } from '../board';
import { StateBlock } from '../tide';
import AtdPanel from './AtdPanel';
import { atdApi, AtdResidentRow, AtdResidentsPage, AtdBucket, ATD_BUCKETS, AtdDispensePlan } from './atdApi';

const DEFAULT_STATUS: AtdBucket = 'any';
const NOT_MARKED = 'Not marked';

const PLAN_WORDS: Record<string, string> = {
  VISIT_OTHER_BUILDING: 'planned at another building; the resident has moved',
  VISIT_CANCELLED: 'that visit was cancelled',
  VISIT_COMPLETED: 'that visit completed without a dispense recorded',
  VISIT_PASSED: 'that visit date passed without a dispense recorded',
  PIT_STOP_PASSED: 'that pit stop date passed',
};

/** The ATD chip a row draws; the legend draws the same component for each bucket. */
function AtdChip({ r }: { r: AtdResidentRow }) {
  const a = r.atd;
  const word = a.status || NOT_MARKED;
  return (
    <span title={a.meaning || (a.status ? undefined : 'No ATD word is recorded. Blank is not a No.')}>
      <StateMark word={word} color={a.color} marker={a.marker || 'hatch'} stateKey={a.bucket} sub={a.recognised ? undefined : 'a word eMerge does not recognise'} />
    </span>
  );
}

function PlanCell({ r }: { r: AtdResidentRow }) {
  if (r.atd.bucket !== 'yes') {
    return <span className="board-na">{r.atd.bucket === 'dispensed' ? 'dispensed' : 'not ATD'}</span>;
  }
  const p: AtdDispensePlan | null = r.dispense_plan;
  if (!p) return <><span>Not yet scheduled</span><SourceTag source="EMERGE" detail="Scheduling" /></>;
  const recorded = p.recorded_state || p.state;
  const words = p.state === 'ON_MANIFEST' ? `On the manifest ${boardDate(p.visit_on) || '(date not recorded)'}`
    : p.state === 'PIT_STOP' ? `Pit stop ${boardDate(p.pit_stop_on) || '(date not recorded)'}`
      : 'Not yet scheduled';
  return (
    <span className="board-dated">
      <span className="board-dated__value">{words}</span>
      {p.attention && (
        <span className="board-tag board-tag--caution" title={`Scheduling recorded ${recorded.toLowerCase().replace(/_/g, ' ')}, but ${PLAN_WORDS[p.attention] || p.attention}.`}>
          Plan again: {PLAN_WORDS[p.attention] || p.attention}
        </span>
      )}
      <SourceTag source="EMERGE" detail="Scheduling" />
    </span>
  );
}

function columns(): BoardColumn<AtdResidentRow>[] {
  return [
    {
      key: 'facility', label: 'Building', sort: 'building', source: 'EMERGE',
      help: 'The resident\'s current confirmed building in eMerge -- the same building that decides who may see and mark them. "No building on file" is listed for an enterprise reader only.',
      render: (r) => (r.facility
        ? <><span className="board-clip" title={r.facility.name || undefined}>{r.facility.name || 'Name not recorded'}</span><SourceTag source="EMERGE" detail="current building" /></>
        : <NotRecorded label="no building on file" />),
    },
    {
      key: 'atd', label: 'ATD', source: 'EMERGE',
      help: 'Approved to dispense: the word eMerge holds for the resident (Yes, Dispensed, No, NO-DECEASED, NO-MOVED, REVERSAL, REFUSED). "Not marked" means no word is recorded; it is not a No. The shape and colour are the legend\'s.',
      render: (r) => <><AtdChip r={r} /><SourceTag source="EMERGE" detail="ATD" /></>,
    },
    {
      key: 'atd_date', label: 'ATD date', sort: 'atd_date', source: 'EMERGE',
      help: 'The date the resident became ATD (Yes). Kept as it stands after a dispense or a later change; the date of those is in the resident\'s ATD history.',
      render: (r) => (r.atd.atd_date ? <>{boardDate(r.atd.atd_date)}<SourceTag source="EMERGE" /></> : <NotRecorded />),
    },
    {
      key: 'plan', label: 'Dispense plan', source: 'EMERGE',
      help: 'For a resident whose ATD is Yes: Scheduling\'s plan to dispense (on a visit\'s manifest, or a pit stop), judged today against the resident\'s current building. "Plan again" means the plan no longer holds. Not shown for any other ATD word.',
      render: (r) => <PlanCell r={r} />,
    },
  ];
}

interface LoadError { message: string; status?: number; code?: string }

/** The bucket an ATD word falls in, as the server reads it (trimmed, case-insensitive; blank is Not marked). */
function bucketOfWord(word: string | null): Exclude<AtdBucket, 'any'> {
  const w = (word || '').trim().toUpperCase();
  if (!w) return 'none';
  if (w === 'YES') return 'yes';
  if (w === 'DISPENSED') return 'dispensed';
  return 'other';
}

function outcome(r: { status?: number; code?: string; message?: string; error?: string }): string {
  if (r.status === undefined) return 'Could not reach the server, so nothing here is current.';
  if (r.status === 403) return 'Listing every resident is for those who mark ATD (a Finance or UMED role with the ATD mark permission). Scheduling\'s ATD queue lists the residents who are ATD.';
  return r.message || r.error || 'The list could not be loaded.';
}

/**
 * Whether to draw the doors to this view: GET /atd/meta's `can.all_residents`
 * (RESIDENT_ATD_MARK, the list's own gate). One read per shell; false while
 * unread and on any failure, so a door is never offered to someone it refuses.
 */
export function useAtdAllResidents(enabled = true): boolean {
  return useAtdCan(enabled).allResidents;
}

/**
 * GET /atd/meta, read once per shell (2026-10-15): `allResidents` draws the doors to this view; `mark` (the ATD
 * command is open to this caller: RESIDENT_ATD_MARK with ATD writes on) offers "Mark ATD…" on the Finance Kanban, so
 * the board makes no second read of its own. Both false while unread and on any failure.
 */
export function useAtdCan(enabled = true): { allResidents: boolean; mark: boolean } {
  const [can, setCan] = useState({ allResidents: false, mark: false });
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    atdApi.meta().then((r) => {
      if (!live) return;
      const c = r && r.success && r.data && r.data.can;
      setCan({ allResidents: Boolean(c && c.all_residents), mark: Boolean(c && c.mark) });
    }).catch(() => undefined);
    return () => { live = false; };
  }, [enabled]);
  return can;
}

export interface AtdResidentsBoardProps {
  /** Prefix for the tab ids (one board per page). */
  idBase?: string;
  /** Where the view is opened from, said in the header line ("Finance", "UMED"). */
  from?: string;
}

export default function AtdResidentsBoard({ idBase = 'atd-all', from }: AtdResidentsBoardProps) {
  const [st, update] = useBoardUrlState(['building'], DEFAULT_STATUS);
  const status: AtdBucket = (ATD_BUCKETS as readonly string[]).includes(st.tab) ? (st.tab as AtdBucket) : DEFAULT_STATUS;
  const [data, setData] = useState<AtdResidentsPage | null>(null);
  const [lastTabs, setLastTabs] = useState<BoardTabDef[]>([]);
  const [lastBuildings, setLastBuildings] = useState<AtdResidentsPage['filter_options']['buildings']>([]);
  const [err, setErr] = useState<LoadError | null>(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(st.q);
  const [changed, setChanged] = useState<{ id: string; name: string; msg: string; status?: string | null; left: 'view' | 'page' | null; settled: boolean; after: number } | null>(null);
  // The number of the newest read that has finished: a change is judged against the read that followed it, never an older one.
  const [loadedSeq, setLoadedSeq] = useState(0);
  const seq = useRef(0);
  useEffect(() => { setDraft(st.q); }, [st.q]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true); setErr(null);
    const r = await atdApi.residents({ q: st.q, building: st.filters.building, status, sort: st.sort, dir: st.dir, page: st.page });
    if (mine !== seq.current) return;
    if (r.success && r.data) {
      setData(r.data);
      setLastTabs(r.data.tabs as unknown as BoardTabDef[]);
      setLastBuildings(r.data.filter_options.buildings);
    } else {
      // Never an empty list on a failure: the rows are dropped and the failure is said.
      setData(null);
      setErr({ message: outcome(r), status: r.status, code: r.code });
    }
    setLoading(false);
    setLoadedSeq(mine);
  }, [st.q, st.filters.building, status, st.sort, st.dir, st.page]);
  useEffect(() => { load(); }, [load]);
  // The newest `load` (the one for the filters on screen now). A panel act can finish after the tab, search, building or sort changed;
  // its callback must re-read what is on screen, never the filters that were current when the dock opened.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  // After an act in the docked panel: re-read the page (the row's word, date and plan, and the counts).
  const onChanged = useCallback((row: AtdResidentRow, msg: string, status?: string | null) => {
    setChanged({ id: row.id, name: row.resident.name, msg, status, left: null, settled: false, after: seq.current + 1 });
    loadRef.current();
  }, []);
  // A resident missing from the re-read page either stopped matching the tab (their new word is in another bucket) or only moved off
  // THIS page (e.g. "every current resident" sorted by ATD date): judge the word, never the absence, and say which; the dock closes either way.
  useEffect(() => {
    if (!changed || changed.settled || loadedSeq < changed.after || !data) return;
    const absent = !data.items.some((r) => r.id === changed.id);
    const stillMatches = status === DEFAULT_STATUS || (changed.status !== undefined && bucketOfWord(changed.status) === status);
    const left = !absent ? null : stillMatches ? 'page' : 'view';
    setChanged({ ...changed, left, settled: true });
    if (left && st.open === changed.id) update({ open: null });
  }, [changed, data, loadedSeq, st.open, status, update]);
  useEffect(() => { setChanged(null); }, [status, st.q, st.filters.building, st.page]);

  const cols = useMemo(() => columns(), []);
  const tabs: BoardTabDef[] = data ? (data.tabs as unknown as BoardTabDef[]) : lastTabs.map((t) => ({ ...t, count: undefined }));
  const activeTab = tabs.find((t) => t.key === status) || null;
  const buildings = data ? data.filter_options.buildings : lastBuildings;
  const filtered = Boolean(st.q || st.filters.building);
  const clear = () => { setDraft(''); update({ q: '', filters: { building: '' } }); };
  const noun = (n: number) => (n === 1 ? 'resident' : 'residents');
  const readOnly = Boolean(data && !data.can.mark && !data.can.dispense);

  const identity = {
    label: 'Resident', sort: 'resident',
    help: 'Every current resident in your scope: not merged into another record, and not Inactive or Deceased -- the residents ATD can be marked for. With or without a Finance account or a UMED case. Open one to see and change their ATD in the panel docked beside the table.',
    render: (r: AtdResidentRow) => (
      <>
        <strong>{r.resident.name}</strong>
        <span className="board-src">status {r.resident.status ? r.resident.status.trim() : 'not recorded'}</span>
      </>
    ),
    name: (r: AtdResidentRow) => r.resident.name,
  };

  return (
    <section className="board board-frame" aria-label="ATD — all residents">
      {tabs.length > 0 && <BoardTabs tabs={tabs} value={status} onChange={(k) => update({ tab: k })} label="ATD status" idBase={idBase} />}
      <div role="tabpanel" id={panelId(idBase)} aria-labelledby={activeTab ? tabId(idBase, activeTab.key) : undefined} className="board-panel">
        <div className="board-head">
          {activeTab && <p className="board-what">{(data && data.what) || activeTab.what}<span className="board-what__src">eMerge ATD{from ? ` · opened from ${from}` : ''}</span></p>}
          <p className="board-basis">
            {data ? data.rule : 'Current residents: not merged into another record, and status not Inactive or Deceased.'}
            {' '}Open a resident to mark ATD, mark dispensed, change it or resend the notices; it is the same ATD record the Finance dossier, the UMED case panel and Scheduling&rsquo;s ATD queue show.
          </p>
          {readOnly && <p className="board-caveat" role="note">Read-only for you: ATD changes are not switched on, or your role can see ATD here but not change it.</p>}
          {changed && (
            <p className="board-caveat" role="status">
              {changed.name}: {changed.msg}{changed.left === 'view' ? ` They no longer match ${activeTab ? `"${activeTab.label}"` : 'this view'}, so they have left it; the counts above include the change.` : ''}{changed.left === 'page' ? ' They still match this view but are no longer on this page (the order moved them); look for them on another page or search by name.' : ''}
            </p>
          )}
          <div className="board-controls">
            <form className="board-search" role="search" onSubmit={(e) => { e.preventDefault(); update({ q: draft.trim() }); }}>
              <label className="board-field"><span>Resident or building</span>
                <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Name or building" aria-label="Search every current resident" maxLength={120} />
              </label>
              <button type="submit" className="board-btn">Find</button>
            </form>
            <label className="board-field">
              <span>Building</span>
              <select value={st.filters.building} onChange={(e) => update({ filters: { building: e.target.value } })} aria-label="Building">
                <option value="">Every building</option>
                {st.filters.building && !buildings.some((b) => b.id === st.filters.building) && <option value={st.filters.building}>Selected (not in this scope)</option>}
                {buildings.map((b) => <option key={b.id} value={b.id}>{`${b.name} (${b.count.toLocaleString('en-US')})`}</option>)}
              </select>
            </label>
            {filtered && <button type="button" className="board-btn" onClick={clear}>Clear search and building</button>}
            {data && (
              <span className="board-result" aria-live="polite">
                <strong>{data.total.toLocaleString('en-US')}</strong> {noun(data.total)} in {activeTab?.label || 'this view'}{filtered ? ' matching your search and building' : ''}{loading ? ' · updating…' : ''}
              </span>
            )}
          </div>
          {data && (
            <BoardLegend
              title="ATD status"
              tabs={tabs}
              allKey={DEFAULT_STATUS}
              units={['resident', 'residents']}
              value={status}
              onSelect={(k) => update({ tab: k })}
              scope="Every current resident is in exactly one of these four, shown by a word, a shape and a colour; the four counts add up to every current resident and follow your search and building. Choose one to list only those residents."
            />
          )}
        </div>

        {!data && loading && !err && <div className="board-state"><StateBlock kind="loading" shape="table" label="Loading every current resident…" /></div>}
        {err && <div className="board-state"><StateBlock kind="error" status={err.status} message={err.message} onRetry={load} /></div>}
        {data && data.items.length === 0 && data.total > 0 && (
          <div className="board-state">
            <StateBlock kind="empty" reason="filtered" message={`Page ${st.page} is past the end: this view has ${data.total.toLocaleString('en-US')} ${noun(data.total)}.`} onClear={() => update({ page: 1 })} clearLabel="Go to page 1" />
          </div>
        )}
        {data && data.items.length === 0 && data.total === 0 && (
          <div className="board-state">
            {filtered
              ? <StateBlock kind="empty" reason="filtered" message={`No current resident in ${activeTab?.label || 'this view'} matches your search and building.`} onClear={clear} clearLabel="Clear search and building" />
              : <StateBlock kind="empty" reason="none" message={`No current resident is in ${activeTab?.label || 'this view'} right now.`} />}
          </div>
        )}
        {data && data.items.length > 0 && (
          <BoardTable<AtdResidentRow>
            label={`ATD — all residents, ${activeTab?.label || status}, ${data.total} ${noun(data.total)}`}
            columns={cols}
            rows={data.items}
            rowKey={(r) => r.id}
            identity={identity}
            openKey={st.open}
            onToggle={(k) => update({ open: st.open === k ? null : k })}
            detailLabel={(r) => `${r.resident.name}: ATD`}
            renderDetail={(r) => (
              <div className="atd-dock">
                <p className="board-basis" style={{ padding: '8px 16px 0' }}>
                  {r.facility ? r.facility.name || 'Building name not recorded' : 'No building on file'} · resident status {r.resident.status ? r.resident.status.trim() : 'not recorded'}
                </p>
                <AtdPanel patientId={r.id} onChanged={(msg, s) => onChanged(r, msg, s)} />
              </div>
            )}
            sort={data.sort} dir={data.dir} sorts={data.sorts}
            onSort={(col, dir) => update({ sort: col, dir })}
            busy={loading}
          />
        )}
        {data && <Pager page={data.page} limit={data.limit} total={data.total} onPage={(p) => update({ page: p })} label="residents" />}
      </div>
      <div className="board-foot">
        {data && activeTab && <p>Counted and listed by one predicate ({activeTab.predicate}); the count on the tab is the number of rows here. &ldquo;not recorded&rdquo; means eMerge holds no value.</p>}
        <ColumnKey columns={cols} identity={identity} />
      </div>
    </section>
  );
}
